import { toast } from 'sonner'
import { useLanguage } from '@/lib/i18n/language-provider'
import type { WorkspaceElement, DataPage, PowerPage, Page, CableRoute, LEDProcessor, LEDPanel, PowerCabinet } from '@/types/synoptique'
import { PORT_COLORS } from '../constants'
import { computeCabinetLoadMap, isCabinetLinkCable } from '../utils/cabling-mutations'
import { calculateExportNumberPosition } from '../utils/export-helpers'
import { imageUrlToBase64 } from '../utils/connection-helpers'
import posthog from 'posthog-js'

interface UseExportSvgParams {
  workspaceRef: React.RefObject<SVGSVGElement | null>
  isTauriApp: boolean
  isTauri: boolean
  isPro: boolean
  isPowerMode: boolean
  backupPorts: Record<string, Record<number, number>>
  backupProcessors: Record<string, string>
  showCartouche: boolean
  cartouche: {
    agence: string
    client: string
    evenement: string
    lieu: string
    suite: string
    quoi: string
    date: string
    de: string
  }
  showExportProcessorLink: boolean
  dataPages: DataPage[]
  powerPages: PowerPage[]
  currentDataPageId: string
  currentPowerPageId: string
  currentDataPage: DataPage
  currentPowerPage: PowerPage
  demoMode: { state: { isDemo: boolean } }
  onboarding: { state: { isActive: boolean }; completeAction: (action: string) => void }
  portColorOverrides: Record<string, Record<number, string>>
  outletColorOverrides: Record<string, Record<number, string>>
  getActualImageBounds: (element: WorkspaceElement) => { x: number; y: number; width: number; height: number }
  isProcessorBackup: (processorId: string) => boolean
  getBackupProcessor: (processorId: string) => string | null
  setIsExporting: (v: boolean) => void
  setExportProgress: (v: string) => void
  setShowExportSuccess: (v: boolean) => void
}

export function useExportSvg({
  workspaceRef,
  isTauriApp,
  isTauri,
  isPro,
  isPowerMode,
  backupPorts,
  backupProcessors,
  showCartouche,
  cartouche,
  showExportProcessorLink,
  dataPages,
  powerPages,
  currentDataPageId,
  currentPowerPageId,
  currentDataPage,
  currentPowerPage,
  demoMode,
  onboarding,
  portColorOverrides,
  outletColorOverrides,
  getActualImageBounds,
  isProcessorBackup,
  getBackupProcessor,
  setIsExporting,
  setExportProgress,
  setShowExportSuccess,
}: UseExportSvgParams) {
  const { t } = useLanguage()

  // Fonction d'assistance pour créer le SVG d'export pour une page Data
  const createExportSvgForDataPage = async (dataPage: DataPage, pageNumber: number, totalPages: number): Promise<SVGSVGElement> => {
    // Convertir DataPage en Page pour la compatibilité avec le code d'export existant
    const page: Page = {
      ...dataPage,
      powerConnections: [], // Pas de connexions power dans une page Data
      mirePowerConnections: []
    }
    return createExportSvgForPage(page, pageNumber, totalPages)
  }

  // Fonction d'assistance pour créer le SVG d'export pour une page Power
  const createExportSvgForPowerPage = async (powerPage: PowerPage, pageNumber: number, totalPages: number): Promise<SVGSVGElement> => {
    // Convertir PowerPage en Page pour la compatibilité avec le code d'export existant
    const page: Page = {
      ...powerPage,
      panelConnections: [], // Pas de connexions de données dans une page Power
      mireConnections: [],
      cableRoutes: powerPage.cableRoutes.filter(cable => cable.type !== 'data') // Exclure les câbles data
    }
    return createExportSvgForPage(page, pageNumber, totalPages)
  }

  const createExportSvgForPage = async (page: Page, pageNumber: number, totalPages: number): Promise<SVGSVGElement> => {
    const originalSvg = workspaceRef.current
    if (!originalSvg) {
      throw new Error("Workspace SVG non trouvé")
    }

    // Utiliser les mêmes couleurs que le workspace pour l'export
    const PORT_COLORS_EXPORT = PORT_COLORS as unknown as string[]

    // Fonction pour obtenir la couleur d'un port — tient compte des overrides custom
    const getPortColorForExport = (portNumber: number, processorId?: string | null) => {
      if (processorId && portColorOverrides[processorId]?.[portNumber]) {
        return portColorOverrides[processorId][portNumber]
      }
      return PORT_COLORS_EXPORT[(portNumber - 1) % PORT_COLORS_EXPORT.length]
    }

    // Fonction pour obtenir la couleur d'une prise — tient compte des overrides custom
    const getOutletColorForExport = (outletNumber: number, cabinetId?: string | null) => {
      if (cabinetId && outletColorOverrides[cabinetId]?.[outletNumber]) {
        return outletColorOverrides[cabinetId][outletNumber]
      }
      return PORT_COLORS_EXPORT[(outletNumber - 1) % PORT_COLORS_EXPORT.length]
    }

    // Marqueurs de flèche pour les couleurs customisées (créés à la demande)
    const customArrowMarkerIds = new Map<string, string>()
    const ensureArrowMarker = (color: string, portNumber: number): string => {
      const paletteIndex = (portNumber - 1) % PORT_COLORS_EXPORT.length
      // Réutiliser le marker de palette si la couleur est la couleur par défaut de ce port
      if (color === PORT_COLORS_EXPORT[paletteIndex]) {
        return `arrowhead-port${paletteIndex + 1}`
      }
      const existing = customArrowMarkerIds.get(color)
      if (existing) return existing
      const markerId = `arrowhead-custom-${customArrowMarkerIds.size + 1}`
      customArrowMarkerIds.set(color, markerId)
      const arrowMarker = document.createElementNS("http://www.w3.org/2000/svg", "marker")
      arrowMarker.setAttribute("id", markerId)
      arrowMarker.setAttribute("markerWidth", "6")
      arrowMarker.setAttribute("markerHeight", "6")
      arrowMarker.setAttribute("refX", "5")
      arrowMarker.setAttribute("refY", "3")
      arrowMarker.setAttribute("orient", "auto")
      arrowMarker.setAttribute("markerUnits", "strokeWidth")
      const arrowPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
      arrowPath.setAttribute("d", "M 0 0 L 6 3 L 0 6 z")
      arrowPath.setAttribute("fill", color)
      arrowMarker.appendChild(arrowPath)
      exportDefs.appendChild(arrowMarker)
      return markerId
    }

    // Créer le SVG de base pour l'export
    const exportSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    exportSvg.setAttribute("width", "860mm")
    exportSvg.setAttribute("height", "600mm")
    exportSvg.setAttribute("viewBox", "-430 -300 860 600")
    exportSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg")

    // 1. Copier les définitions (grille, flèches, markers)
    const defs = originalSvg.querySelector("defs")
    let exportDefs: SVGDefsElement

    if (defs) {
      exportDefs = defs.cloneNode(true) as SVGDefsElement

      // Filtre drop-shadow pour les câbles (lisibilité)
      const cableShadow = document.createElementNS("http://www.w3.org/2000/svg", "filter")
      cableShadow.setAttribute("id", "cable-shadow")
      cableShadow.setAttribute("x", "-20%")
      cableShadow.setAttribute("y", "-20%")
      cableShadow.setAttribute("width", "140%")
      cableShadow.setAttribute("height", "140%")
      cableShadow.innerHTML = '<feDropShadow dx="0" dy="0" stdDeviation="1.5" flood-color="rgba(0,0,0,0.4)"/>'
      exportDefs.appendChild(cableShadow)
      exportSvg.appendChild(exportDefs)

      // Créer des markers de flèche pour chaque couleur de port - plus fines
      PORT_COLORS_EXPORT.forEach((color, index) => {
        const markerId = `arrowhead-port${index + 1}`
        if (!exportDefs.querySelector(`#${markerId}`)) {
          const arrowMarker = document.createElementNS("http://www.w3.org/2000/svg", "marker")
          arrowMarker.setAttribute("id", markerId)
          arrowMarker.setAttribute("markerWidth", "6")
          arrowMarker.setAttribute("markerHeight", "6")
          arrowMarker.setAttribute("refX", "5")
          arrowMarker.setAttribute("refY", "3")
          arrowMarker.setAttribute("orient", "auto")
          arrowMarker.setAttribute("markerUnits", "strokeWidth")
          const arrowPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
          arrowPath.setAttribute("d", "M 0 0 L 6 3 L 0 6 z")
          arrowPath.setAttribute("fill", color)
          arrowMarker.appendChild(arrowPath)
          exportDefs.appendChild(arrowMarker)
        }
      })
    } else {
      // Créer les définitions de base si elles n'existent pas
      exportDefs = document.createElementNS("http://www.w3.org/2000/svg", "defs")

      // Grille
      const gridPattern = document.createElementNS("http://www.w3.org/2000/svg", "pattern")
      gridPattern.setAttribute("id", "grid")
      gridPattern.setAttribute("width", "50")
      gridPattern.setAttribute("height", "50")
      gridPattern.setAttribute("patternUnits", "userSpaceOnUse")
      const gridPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
      gridPath.setAttribute("d", "M 50 0 L 0 0 0 50")
      gridPath.setAttribute("fill", "none")
      gridPath.setAttribute("stroke", "#888")
      gridPath.setAttribute("stroke-width", "0.5")
      gridPath.setAttribute("opacity", "0.3")
      gridPattern.appendChild(gridPath)
      exportDefs.appendChild(gridPattern)

      // Créer des markers de flèche pour chaque couleur de port - plus fines
      PORT_COLORS_EXPORT.forEach((color, index) => {
        const arrowMarker = document.createElementNS("http://www.w3.org/2000/svg", "marker")
        arrowMarker.setAttribute("id", `arrowhead-port${index + 1}`)
        arrowMarker.setAttribute("markerWidth", "6")
        arrowMarker.setAttribute("markerHeight", "6")
        arrowMarker.setAttribute("refX", "5")
        arrowMarker.setAttribute("refY", "3")
        arrowMarker.setAttribute("orient", "auto")
        arrowMarker.setAttribute("markerUnits", "strokeWidth")
        const arrowPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
        arrowPath.setAttribute("d", "M 0 0 L 6 3 L 0 6 z")
        arrowPath.setAttribute("fill", color)
        arrowMarker.appendChild(arrowPath)
        exportDefs.appendChild(arrowMarker)
      })

      exportSvg.appendChild(exportDefs)
    }

    // 2. Ajouter le fond blanc et la grille
    const background = document.createElementNS("http://www.w3.org/2000/svg", "rect")
    background.setAttribute("x", "-430")
    background.setAttribute("y", "-300")
    background.setAttribute("width", "860")
    background.setAttribute("height", "600")
    background.setAttribute("fill", "white")
    exportSvg.appendChild(background)

    const gridRect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
    gridRect.setAttribute("x", "-430")
    gridRect.setAttribute("y", "-300")
    gridRect.setAttribute("width", "860")
    gridRect.setAttribute("height", "600")
    gridRect.setAttribute("fill", "url(#grid)")
    gridRect.setAttribute("opacity", "0.1")
    exportSvg.appendChild(gridRect)

    // 3. Pré-fetcher les images en base64 (SVG en blob URL ne peut pas charger des ressources relatives ni CORS)
    const PLACEHOLDER_URL = '/images/processors/placeholder.png'
    const imageBase64Cache: Record<string, string> = {}
    const imageUrls = [...new Set([
      ...page.workspaceElements
        .filter(el => el.type === 'processor' && (el.data as LEDProcessor).image)
        .map(el => (el.data as LEDProcessor).image as string),
      PLACEHOLDER_URL
    ])]
    await Promise.all(imageUrls.map(async (url) => {
      imageBase64Cache[url] = await imageUrlToBase64(url)
    }))

    // Groupe overlay pour les labels PX/RX/order (rendu après tous les câbles)
    const pxOverlayGroup = document.createElementNS("http://www.w3.org/2000/svg", "g")
    pxOverlayGroup.setAttribute("data-role", "px-overlay")

    // 4. Rendre les éléments de la page spécifique
    // On doit recréer les éléments du workspace pour cette page
    page.workspaceElements.forEach(element => {
      const group = document.createElementNS("http://www.w3.org/2000/svg", "g")
      group.setAttribute("data-element-id", element.id)
      group.setAttribute("transform", `translate(${element.position.x}, ${element.position.y})`)

      if (element.type === 'processor') {
        const processor = element.data as LEDProcessor

        // Calculer les connexions Main et Backup (panneaux + mires)
        const processorPanelConnections = page.panelConnections.filter(conn => conn.processorId === element.id)
        const processorMireConnections = (page.mireConnections || []).filter((conn: any) => conn.processorId === element.id)
        const allProcessorConnections = [
          ...processorPanelConnections.map(conn => ({ port: conn.port })),
          ...processorMireConnections.map((conn: any) => ({ port: conn.port }))
        ]

        const processorBackupPorts = backupPorts[element.id] || {}
        const backupPortNumbers = Object.values(processorBackupPorts)

        const mainConnections = allProcessorConnections.filter(conn => !backupPortNumbers.includes(conn.port))

        // Compter les ports Main uniques utilisés
        const mainPortsUsed = new Set(mainConnections.map(conn => conn.port)).size

        // Compter les Backups : uniquement ceux dont le port principal (main) est utilisé
        const usedMainPorts = new Set(mainConnections.map(conn => conn.port))
        const backupPortsUsed = Object.keys(processorBackupPorts).filter(mainPort =>
          usedMainPorts.has(Number(mainPort))
        ).length

        // Nombre total de ports du processeur
        const totalPorts = typeof processor?.outputPorts === 'number' ? processor.outputPorts : 0

        // Statut backup du processeur
        const isBackup = isProcessorBackup(element.id)
        const hasBackupProcessor = getBackupProcessor(element.id)

        // Si le processeur a une image, utiliser le base64 converti — fallback vers placeholder si manquante
        const rawHref = processor.image ? imageBase64Cache[processor.image] : undefined
        const resolvedImageHref = rawHref?.startsWith('data:') ? rawHref : imageBase64Cache[PLACEHOLDER_URL]
        const hasValidImage = resolvedImageHref?.startsWith('data:')
        if (hasValidImage && resolvedImageHref) {
          const image = document.createElementNS("http://www.w3.org/2000/svg", "image")
          image.setAttribute("x", "0")
          image.setAttribute("y", "0")
          image.setAttribute("width", element.size.width.toString())
          image.setAttribute("height", element.size.height.toString())
          image.setAttribute("href", resolvedImageHref)
          image.setAttribute("preserveAspectRatio", "xMidYMid meet")
          group.appendChild(image)

          // Calculer le bas réel de l'image (preserveAspectRatio peut créer des marges)
          // getActualImageBounds retourne des coordonnées absolues, on recentre dans le groupe
          const actualBounds = getActualImageBounds(element)
          const imgBottom = (actualBounds.y - element.position.y) + actualBounds.height
          const imgCenterX = (actualBounds.x - element.position.x) + actualBounds.width / 2

          // Texte du nom du processeur (collé sous l'image)
          const nameText = document.createElementNS("http://www.w3.org/2000/svg", "text")
          nameText.setAttribute("x", imgCenterX.toString())
          nameText.setAttribute("y", (imgBottom + 3).toString())
          nameText.setAttribute("dominant-baseline", "hanging")
          nameText.setAttribute("text-anchor", "middle")
          nameText.setAttribute("font-family", "Arial, sans-serif")
          nameText.setAttribute("font-size", "11")
          nameText.setAttribute("font-weight", "600")
          nameText.setAttribute("fill", "#333333")
          nameText.textContent = processor.customName || `${processor.brand} ${processor.model}`
          group.appendChild(nameText)

          // Label selon statut backup
          const infoText = document.createElementNS("http://www.w3.org/2000/svg", "text")
          infoText.setAttribute("x", imgCenterX.toString())
          infoText.setAttribute("y", (imgBottom + 16).toString())
          infoText.setAttribute("dominant-baseline", "hanging")
          infoText.setAttribute("text-anchor", "middle")
          infoText.setAttribute("font-family", "Arial, sans-serif")
          infoText.setAttribute("font-size", "10")
          if (isBackup) {
            infoText.setAttribute("fill", "#EF4444")
            infoText.setAttribute("font-weight", "bold")
            infoText.textContent = `Backup processor | ${totalPorts} ports`
          } else if (hasBackupProcessor) {
            infoText.setAttribute("fill", "#3B82F6")
            infoText.setAttribute("font-weight", "bold")
            infoText.textContent = `Main processor | ${totalPorts} ports`
          } else {
            infoText.setAttribute("fill", "#666666")
            infoText.textContent = `Main: ${mainPortsUsed} | Backup: ${backupPortsUsed}`
          }
          group.appendChild(infoText)
        } else {
          // Sinon, afficher le rectangle avec le texte
          const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
          rect.setAttribute("width", element.size.width.toString())
          rect.setAttribute("height", element.size.height.toString())
          rect.setAttribute("fill", isBackup ? "#7F1D1D" : "#4A5568")
          rect.setAttribute("stroke", isBackup ? "#EF4444" : "#2D3748")
          rect.setAttribute("stroke-width", "2")
          rect.setAttribute("rx", "8")
          group.appendChild(rect)

          // Nom du processeur en haut du rectangle
          const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
          text.setAttribute("x", (element.size.width / 2).toString())
          text.setAttribute("y", (element.size.height / 2 - 8).toString())
          text.setAttribute("text-anchor", "middle")
          text.setAttribute("dominant-baseline", "middle")
          text.setAttribute("font-family", "Arial, sans-serif")
          text.setAttribute("font-size", "12")
          text.setAttribute("font-weight", "bold")
          text.setAttribute("fill", "white")
          text.textContent = processor.model
          group.appendChild(text)

          // Label selon statut backup
          const infoText = document.createElementNS("http://www.w3.org/2000/svg", "text")
          infoText.setAttribute("x", (element.size.width / 2).toString())
          infoText.setAttribute("y", (element.size.height / 2 + 8).toString())
          infoText.setAttribute("text-anchor", "middle")
          infoText.setAttribute("font-family", "Arial, sans-serif")
          infoText.setAttribute("font-size", "10")
          if (isBackup) {
            infoText.setAttribute("fill", "#FCA5A5")
            infoText.setAttribute("font-weight", "bold")
            infoText.textContent = `Backup processor | ${totalPorts} ports`
          } else if (hasBackupProcessor) {
            infoText.setAttribute("fill", "#93C5FD")
            infoText.setAttribute("font-weight", "bold")
            infoText.textContent = `Main processor | ${totalPorts} ports`
          } else {
            infoText.setAttribute("fill", "white")
            infoText.textContent = `Main: ${mainPortsUsed} | Backup: ${backupPortsUsed}`
          }
          group.appendChild(infoText)
        }
      } else if (element.type === 'panel') {
        const panel = element.data as LEDPanel
        const hasMireImage = panel.mireImage && panel.mireImagePortion

        if (hasMireImage) {
          // Créer un clipPath pour découper l'image
          const clipPath = document.createElementNS("http://www.w3.org/2000/svg", "clipPath")
          clipPath.setAttribute("id", `clip-export-${element.id}`)
          const clipRect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
          clipRect.setAttribute("x", "0")
          clipRect.setAttribute("y", "0")
          clipRect.setAttribute("width", element.size.width.toString())
          clipRect.setAttribute("height", element.size.height.toString())
          clipPath.appendChild(clipRect)

          // Ajouter le clipPath aux defs du SVG parent
          const defsEl = exportSvg.querySelector("defs") || document.createElementNS("http://www.w3.org/2000/svg", "defs")
          if (!exportSvg.querySelector("defs")) {
            exportSvg.insertBefore(defsEl, exportSvg.firstChild)
          }
          defsEl.appendChild(clipPath)

          // Image de la mire (portion correspondante)
          const image = document.createElementNS("http://www.w3.org/2000/svg", "image")
          const imageX = -(panel.mireImagePortion.x / 100) * element.size.width * (100 / panel.mireImagePortion.width)
          const imageY = -(panel.mireImagePortion.y / 100) * element.size.height * (100 / panel.mireImagePortion.height)
          const imageWidth = element.size.width * (100 / panel.mireImagePortion.width)
          const imageHeight = element.size.height * (100 / panel.mireImagePortion.height)

          image.setAttribute("x", imageX.toString())
          image.setAttribute("y", imageY.toString())
          image.setAttribute("width", imageWidth.toString())
          image.setAttribute("height", imageHeight.toString())
          image.setAttribute("href", panel.mireImage)
          image.setAttribute("preserveAspectRatio", "none")
          image.setAttribute("opacity", (panel.opacity !== undefined ? panel.opacity : 1).toString())
          image.setAttribute("clip-path", `url(#clip-export-${element.id})`)
          group.appendChild(image)

          // Bordure du panneau
          const borderRect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
          borderRect.setAttribute("width", element.size.width.toString())
          borderRect.setAttribute("height", element.size.height.toString())
          borderRect.setAttribute("fill", "none")
          borderRect.setAttribute("stroke", "#2D2D2D")
          borderRect.setAttribute("stroke-width", "2")
          group.appendChild(borderRect)
        } else {
          // Rectangle de la dalle classique - Style COEX VMP
          const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
          rect.setAttribute("width", element.size.width.toString())
          rect.setAttribute("height", element.size.height.toString())
          rect.setAttribute("fill", "#B8B8B8")
          rect.setAttribute("fill-opacity", (panel.opacity !== undefined ? panel.opacity : 1).toString())
          rect.setAttribute("stroke", "#2D2D2D")
          rect.setAttribute("stroke-width", "2")
          group.appendChild(rect)
        }

        // Chercher les connexions pour cette dalle
        const panelConns = page.panelConnections.filter(c => c.panelId === element.id)
        const powerConns = page.powerConnections.filter(c => c.panelId === element.id)

        // NEW: Afficher SEULEMENT le premier et dernier panneau pour les connexions Data
        if (panelConns.length > 0) {
          panelConns.forEach(connection => {
            const allPortConnections = page.panelConnections.filter(conn =>
              conn.processorId === connection.processorId &&
              conn.port === connection.port
            ).sort((a, b) => a.order - b.order)

            const isFirstPanel = allPortConnections[0]?.panelId === element.id
            const isLastPanel = allPortConnections[allPortConnections.length - 1]?.panelId === element.id

            // Only render first or last panels
            if (!isFirstPanel && !isLastPanel) return

            // Badge coloré pour le premier panneau - Style identique au workspace
            if (isFirstPanel) {
              const badge = document.createElementNS("http://www.w3.org/2000/svg", "rect")
              badge.setAttribute("width", element.size.width.toString())
              badge.setAttribute("height", element.size.height.toString())
              badge.setAttribute("fill", getPortColorForExport(connection.port, connection.processorId))
              badge.setAttribute("fill-opacity", "0.8")
              badge.setAttribute("rx", "4")
              group.appendChild(badge)

              // Label PX - taille augmentée pour correspondre au workspace
              const label = document.createElementNS("http://www.w3.org/2000/svg", "text")
              label.setAttribute("x", (element.size.width / 2).toString())
              label.setAttribute("y", (element.size.height / 2).toString())
              label.setAttribute("text-anchor", "middle")
              label.setAttribute("dominant-baseline", "central")
              label.setAttribute("font-family", "Arial, sans-serif")
              label.setAttribute("font-size", Math.max(12, element.size.width * 0.35).toString())
              label.setAttribute("font-weight", "bold")
              label.setAttribute("fill", "white")
              label.textContent = `P${connection.port}`
              group.appendChild(label)
            }

            // Badge RX pour le dernier panneau avec backup
            if (isLastPanel) {
              const processorBackups = backupPorts[connection.processorId] || {}
              const backupPort = processorBackups[connection.port]

              if (backupPort) {
                const badge = document.createElementNS("http://www.w3.org/2000/svg", "rect")
                badge.setAttribute("width", element.size.width.toString())
                badge.setAttribute("height", element.size.height.toString())
                badge.setAttribute("fill", getPortColorForExport(backupPort, connection.processorId))
                badge.setAttribute("fill-opacity", "0.8")
                badge.setAttribute("rx", "4")
                group.appendChild(badge)

                const label = document.createElementNS("http://www.w3.org/2000/svg", "text")
                label.setAttribute("x", (element.size.width / 2).toString())
                label.setAttribute("y", (element.size.height / 2).toString())
                label.setAttribute("text-anchor", "middle")
                label.setAttribute("dominant-baseline", "central")
                label.setAttribute("font-family", "Arial, sans-serif")
                label.setAttribute("font-size", Math.max(12, element.size.width * 0.35).toString())
                label.setAttribute("font-weight", "bold")
                label.setAttribute("fill", "white")
                label.textContent = `R${backupPort}`
                group.appendChild(label)
              }
            }

            // Numéro d'ordre avec positionnement intelligent
            const numberPos = calculateExportNumberPosition(element, connection, page.cableRoutes)
            const numberText = document.createElementNS("http://www.w3.org/2000/svg", "text")
            numberText.setAttribute("x", numberPos.x.toString())
            numberText.setAttribute("y", numberPos.y.toString())
            numberText.setAttribute("text-anchor", numberPos.textAnchor)
            numberText.setAttribute("font-family", "Arial, sans-serif")
            numberText.setAttribute("font-size", Math.max(8, element.size.width * 0.27).toString())
            numberText.setAttribute("font-weight", "bold")
            numberText.setAttribute("fill", isFirstPanel ? "white" : numberPos.fill)
            numberText.setAttribute("dominant-baseline", "central")
            numberText.textContent = connection.order.toString()
            group.appendChild(numberText)
          })
        }
        // NEW: Afficher SEULEMENT le premier et dernier panneau pour les connexions Power
        else if (powerConns.length > 0) {
          powerConns.forEach(connection => {
            const allOutletConnections = page.powerConnections.filter(conn =>
              conn.cabinetId === connection.cabinetId &&
              conn.outlet === connection.outlet
            ).sort((a, b) => a.order - b.order)

            const isFirstInChain = allOutletConnections[0]?.panelId === element.id
            const isLastInChain = allOutletConnections[allOutletConnections.length - 1]?.panelId === element.id

            // Only render first or last panels
            if (!isFirstInChain && !isLastInChain) return

            // Badge coloré pour le premier panneau - Style identique au workspace
            if (isFirstInChain) {
              const badge = document.createElementNS("http://www.w3.org/2000/svg", "rect")
              badge.setAttribute("width", element.size.width.toString())
              badge.setAttribute("height", element.size.height.toString())
              badge.setAttribute("fill", getOutletColorForExport(connection.outlet, connection.cabinetId))
              badge.setAttribute("fill-opacity", "0.8")
              badge.setAttribute("rx", "4")
              group.appendChild(badge)

              // Label PC16
              const label = document.createElementNS("http://www.w3.org/2000/svg", "text")
              label.setAttribute("x", (element.size.width / 2).toString())
              label.setAttribute("y", (element.size.height / 2 - 5).toString())
              label.setAttribute("text-anchor", "middle")
              label.setAttribute("font-family", "Arial, sans-serif")
              label.setAttribute("font-size", Math.max(8, element.size.width * 0.25).toString())
              label.setAttribute("font-weight", "bold")
              label.setAttribute("fill", "white")
              label.textContent = `PC${connection.outlet}`
              group.appendChild(label)
            }

            // Numéro d'ordre avec positionnement intelligent
            const pseudoConnection = {
              panelId: connection.panelId,
              processorId: connection.cabinetId,
              port: connection.outlet,
              order: connection.order
            }
            const numberPos = calculateExportNumberPosition(element, pseudoConnection, page.cableRoutes)
            const numberText = document.createElementNS("http://www.w3.org/2000/svg", "text")
            numberText.setAttribute("x", numberPos.x.toString())
            numberText.setAttribute("y", numberPos.y.toString())
            numberText.setAttribute("text-anchor", numberPos.textAnchor)
            numberText.setAttribute("font-family", "Arial, sans-serif")
            numberText.setAttribute("font-size", Math.max(8, element.size.width * 0.27).toString())
            numberText.setAttribute("font-weight", "bold")
            numberText.setAttribute("fill", isFirstInChain ? "white" : numberPos.fill)
            numberText.setAttribute("dominant-baseline", "central")
            numberText.textContent = connection.order.toString()
            group.appendChild(numberText)
          })
        }
        // Pas de connexion : ne rien afficher (pas de numérotation pour les panneaux sans câblage)
      } else if (element.type === 'cabinet') {
        const cabinet = element.data as PowerCabinet
        // Rectangle du coffret
        const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
        rect.setAttribute("width", element.size.width.toString())
        rect.setAttribute("height", element.size.height.toString())
        rect.setAttribute("fill", "#f0f0f0")
        rect.setAttribute("stroke", "black")
        rect.setAttribute("stroke-width", "1")
        rect.setAttribute("rx", "4")
        group.appendChild(rect)

        // Nom du coffret
        const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
        text.setAttribute("x", (element.size.width / 2).toString())
        text.setAttribute("y", (element.size.height / 2 + 5).toString())
        text.setAttribute("text-anchor", "middle")
        text.setAttribute("font-family", "Arial, sans-serif")
        text.setAttribute("font-size", "10")
        text.setAttribute("font-weight", "bold")
        text.setAttribute("fill", "black")
        text.textContent = cabinet.name || element.label || ""
        group.appendChild(text)
      } else if (element.type === 'image' && element.data?.mireConfig) {
        // NEW: Render mire image with virtual cells in export
        const config = element.data.mireConfig
        const { columns, rows } = config.ledWallMode

        // Base mire image (pxOverlayGroup est déclaré avant le forEach)
        const image = document.createElementNS("http://www.w3.org/2000/svg", "image")
        image.setAttribute("x", "0")
        image.setAttribute("y", "0")
        image.setAttribute("width", element.size.width.toString())
        image.setAttribute("height", element.size.height.toString())
        image.setAttribute("href", element.data.imageData)
        image.setAttribute("preserveAspectRatio", "none")
        image.setAttribute("opacity", "0.9")
        group.appendChild(image)

        // Calculate cell dimensions
        const cellWidth = element.size.width / columns
        const cellHeight = element.size.height / rows

        // Get mire connections for this image
        const mireConns = page.mireConnections?.filter(conn => conn.imageId === element.id) || []
        const mirePowerConns = page.mirePowerConnections?.filter(conn => conn.imageId === element.id) || []

        // Helper: créer un élément texte numéro en coin libre
        const createCellNumberText = (
          cx: number, cy: number, cw: number, ch: number,
          order: number, color: string,
          cablePoints: Array<{x: number; y: number}>
        ) => {
          // Choisir le coin le moins encombré par les câbles
          const directions = { top: false, bottom: false, left: false, right: false }
          const center = { x: cx + cw / 2, y: cy + ch / 2 }
          cablePoints.forEach(pt => {
            const dx = pt.x - center.x; const dy = pt.y - center.y
            if (Math.abs(dx) > Math.abs(dy)) { if (dx > 0) directions.right = true; else directions.left = true }
            else { if (dy > 0) directions.bottom = true; else directions.top = true }
          })
          const corners = [
            { blocked: directions.top || directions.left,    x: cx + 5,      y: cy + 12,       anchor: 'start' },
            { blocked: directions.top || directions.right,   x: cx + cw - 5, y: cy + 12,       anchor: 'end' },
            { blocked: directions.bottom || directions.left, x: cx + 5,      y: cy + ch - 5,   anchor: 'start' },
            { blocked: directions.bottom || directions.right,x: cx + cw - 5, y: cy + ch - 5,   anchor: 'end' }
          ]
          const best = corners.find(c => !c.blocked) || corners[0]
          const tEl = document.createElementNS("http://www.w3.org/2000/svg", "text")
          tEl.setAttribute("x", best.x.toString())
          tEl.setAttribute("y", best.y.toString())
          tEl.setAttribute("text-anchor", best.anchor)
          tEl.setAttribute("font-family", "Arial, sans-serif")
          tEl.setAttribute("font-size", Math.max(10, Math.min(14, cw * 0.27)).toString())
          tEl.setAttribute("font-weight", "bold")
          tEl.setAttribute("fill", color)
          tEl.setAttribute("dominant-baseline", "central")
          tEl.setAttribute("style", "text-shadow: 0 0 3px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.6)")
          tEl.textContent = order.toString()
          return tEl
        }

        // Group data connections by processor+port — rendre TOUTES les cellules
        const allConnectionsByPort: Record<string, typeof mireConns> = {}
        mireConns.forEach(conn => {
          const key = `${conn.processorId}-${conn.port}`
          if (!allConnectionsByPort[key]) allConnectionsByPort[key] = []
          allConnectionsByPort[key].push(conn)
        })

        Object.entries(allConnectionsByPort).forEach(([_key, connections]) => {
          const sorted = connections.sort((a, b) => a.order - b.order)
          const firstConn = sorted[0]
          const lastConn = sorted[sorted.length - 1]

          sorted.forEach(conn => {
            const x = (conn.gridPosition.col - 1) * cellWidth
            const y = (conn.gridPosition.row - 1) * cellHeight
            const cellColor = getPortColorForExport(conn.port, conn.processorId)
            const isFirst = conn === firstConn
            const isLast = conn === lastConn

            // Fond coloré semi-transparent sur toutes les cellules connectées
            const cellBg = document.createElementNS("http://www.w3.org/2000/svg", "rect")
            cellBg.setAttribute("x", x.toString()); cellBg.setAttribute("y", y.toString())
            cellBg.setAttribute("width", cellWidth.toString()); cellBg.setAttribute("height", cellHeight.toString())
            const processorBackupsCheck = backupPorts[conn.processorId] || {}
            const hasBackupForCell = isLast && !!processorBackupsCheck[conn.port]
            cellBg.setAttribute("fill", cellColor); cellBg.setAttribute("fill-opacity", (isFirst || hasBackupForCell) ? "1" : "0.25")
            cellBg.setAttribute("stroke", cellColor); cellBg.setAttribute("stroke-width", "1"); cellBg.setAttribute("stroke-opacity", "0.6")
            cellBg.setAttribute("rx", "2")
            group.appendChild(cellBg)

            // Offset absolu dans l'overlay (le groupe est positionné en position absolue)
            const absX = element.position.x + x
            const absY = element.position.y + y

            // Label PX sur 1ère cellule → overlay
            if (isFirst) {
              const label = document.createElementNS("http://www.w3.org/2000/svg", "text")
              label.setAttribute("x", (absX + cellWidth / 2).toString()); label.setAttribute("y", (absY + cellHeight / 2).toString())
              label.setAttribute("text-anchor", "middle"); label.setAttribute("dominant-baseline", "central")
              label.setAttribute("font-family", "Arial, sans-serif")
              label.setAttribute("font-size", Math.max(10, Math.min(14, cellWidth * 0.3)).toString())
              label.setAttribute("font-weight", "bold"); label.setAttribute("fill", "white")
              label.textContent = `P${conn.port}`
              pxOverlayGroup.appendChild(label)
            }

            // Badge RX sur dernière cellule si backup → overlay
            if (isLast) {
              const processorBackups = backupPorts[conn.processorId] || {}
              const backupPort = processorBackups[conn.port]
              if (backupPort) {
                const badge = document.createElementNS("http://www.w3.org/2000/svg", "rect")
                badge.setAttribute("x", absX.toString()); badge.setAttribute("y", absY.toString())
                badge.setAttribute("width", cellWidth.toString()); badge.setAttribute("height", cellHeight.toString())
                badge.setAttribute("fill", getPortColorForExport(backupPort, conn.processorId)); badge.setAttribute("fill-opacity", "1"); badge.setAttribute("rx", "2")
                pxOverlayGroup.appendChild(badge)
                const label = document.createElementNS("http://www.w3.org/2000/svg", "text")
                label.setAttribute("x", (absX + cellWidth / 2).toString()); label.setAttribute("y", (absY + cellHeight / 2).toString())
                label.setAttribute("text-anchor", "middle"); label.setAttribute("dominant-baseline", "central")
                label.setAttribute("font-family", "Arial, sans-serif")
                label.setAttribute("font-size", Math.max(10, Math.min(14, cellWidth * 0.3)).toString())
                label.setAttribute("font-weight", "bold"); label.setAttribute("fill", "white")
                label.textContent = `R${backupPort}`
                pxOverlayGroup.appendChild(label)
              }
            }

            // Numéro d'ordre (toutes cellules sauf 1ère) → overlay, coords absolues comme le workspace
            if (!isFirst) {
              const cellId = `${element.id}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
              const cables = page.cableRoutes.filter(c => c.from === cellId || c.to === cellId)
              const directions = { top: false, bottom: false, left: false, right: false }
              const cellCenter = { x: absX + cellWidth / 2, y: absY + cellHeight / 2 }
              cables.forEach(cable => {
                const isIncoming = cable.to === cellId
                const otherPt = isIncoming ? cable.points[0] : cable.points[cable.points.length - 1]
                if (!otherPt) return
                const dx = otherPt.x - cellCenter.x; const dy = otherPt.y - cellCenter.y
                if (Math.abs(dx) > Math.abs(dy)) { if (dx > 0) directions.right = true; else directions.left = true }
                else { if (dy > 0) directions.bottom = true; else directions.top = true }
              })
              const corners = [
                { blocked: directions.top || directions.left,     x: absX + 8,              y: absY + 12,               anchor: 'start' },
                { blocked: directions.top || directions.right,    x: absX + cellWidth - 8,  y: absY + 12,               anchor: 'end' },
                { blocked: directions.bottom || directions.left,  x: absX + 8,              y: absY + cellHeight - 8,   anchor: 'start' },
                { blocked: directions.bottom || directions.right, x: absX + cellWidth - 8,  y: absY + cellHeight - 8,   anchor: 'end' }
              ]
              const best = corners.find(c => !c.blocked) || corners[0]
              const numText = document.createElementNS("http://www.w3.org/2000/svg", "text")
              numText.setAttribute("x", best.x.toString())
              numText.setAttribute("y", best.y.toString())
              numText.setAttribute("text-anchor", best.anchor)
              numText.setAttribute("font-family", "Arial, sans-serif")
              numText.setAttribute("font-size", Math.max(12, Math.min(16, cellWidth * 0.27)).toString())
              numText.setAttribute("font-weight", "bold")
              numText.setAttribute("fill", cellColor)
              numText.setAttribute("dominant-baseline", "middle")
              numText.setAttribute("style", "text-shadow: 0 0 3px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.6)")
              numText.textContent = conn.order.toString()
              pxOverlayGroup.appendChild(numText)
            }
          })
        })

        // Idem pour les connexions Power — rendre TOUTES les cellules
        const allPowerConnectionsByOutlet: Record<string, typeof mirePowerConns> = {}
        mirePowerConns.forEach(conn => {
          const key = `${conn.cabinetId}-${conn.outlet}`
          if (!allPowerConnectionsByOutlet[key]) allPowerConnectionsByOutlet[key] = []
          allPowerConnectionsByOutlet[key].push(conn)
        })

        Object.entries(allPowerConnectionsByOutlet).forEach(([_key, connections]) => {
          const sorted = connections.sort((a, b) => a.order - b.order)
          const firstConn = sorted[0]

          sorted.forEach(conn => {
            const x = (conn.gridPosition.col - 1) * cellWidth
            const y = (conn.gridPosition.row - 1) * cellHeight
            const cellColor = getOutletColorForExport(conn.outlet, conn.cabinetId)
            const isFirst = conn === firstConn

            // Fond coloré semi-transparent sur toutes les cellules power connectées
            const cellBg = document.createElementNS("http://www.w3.org/2000/svg", "rect")
            cellBg.setAttribute("x", x.toString()); cellBg.setAttribute("y", y.toString())
            cellBg.setAttribute("width", cellWidth.toString()); cellBg.setAttribute("height", cellHeight.toString())
            cellBg.setAttribute("fill", cellColor); cellBg.setAttribute("fill-opacity", isFirst ? "1" : "0.25")
            cellBg.setAttribute("stroke", cellColor); cellBg.setAttribute("stroke-width", "1"); cellBg.setAttribute("stroke-opacity", "0.6")
            cellBg.setAttribute("rx", "2")
            group.appendChild(cellBg)

            const absXp = element.position.x + x
            const absYp = element.position.y + y

            // Label PC sur 1ère cellule → overlay
            if (isFirst) {
              const label = document.createElementNS("http://www.w3.org/2000/svg", "text")
              label.setAttribute("x", (absXp + cellWidth / 2).toString()); label.setAttribute("y", (absYp + cellHeight / 2).toString())
              label.setAttribute("text-anchor", "middle"); label.setAttribute("dominant-baseline", "central")
              label.setAttribute("font-family", "Arial, sans-serif")
              label.setAttribute("font-size", Math.max(10, Math.min(14, cellWidth * 0.3)).toString())
              label.setAttribute("font-weight", "bold"); label.setAttribute("fill", "white")
              label.textContent = `PC${conn.outlet}`
              pxOverlayGroup.appendChild(label)
            }

            // Numéro sur toutes les cellules sauf 1ère → overlay, coords absolues
            if (!isFirst) {
              const cellId = `${element.id}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
              const cables = page.cableRoutes.filter(c => c.from === cellId || c.to === cellId)
              const directionsP = { top: false, bottom: false, left: false, right: false }
              const cellCenterP = { x: absXp + cellWidth / 2, y: absYp + cellHeight / 2 }
              cables.forEach(cable => {
                const isIncoming = cable.to === cellId
                const otherPt = isIncoming ? cable.points[0] : cable.points[cable.points.length - 1]
                if (!otherPt) return
                const dx = otherPt.x - cellCenterP.x; const dy = otherPt.y - cellCenterP.y
                if (Math.abs(dx) > Math.abs(dy)) { if (dx > 0) directionsP.right = true; else directionsP.left = true }
                else { if (dy > 0) directionsP.bottom = true; else directionsP.top = true }
              })
              const cornersP = [
                { blocked: directionsP.top || directionsP.left,     x: absXp + 8,              y: absYp + 12,               anchor: 'start' },
                { blocked: directionsP.top || directionsP.right,    x: absXp + cellWidth - 8,  y: absYp + 12,               anchor: 'end' },
                { blocked: directionsP.bottom || directionsP.left,  x: absXp + 8,              y: absYp + cellHeight - 8,   anchor: 'start' },
                { blocked: directionsP.bottom || directionsP.right, x: absXp + cellWidth - 8,  y: absYp + cellHeight - 8,   anchor: 'end' }
              ]
              const bestP = cornersP.find(c => !c.blocked) || cornersP[0]
              const numText = document.createElementNS("http://www.w3.org/2000/svg", "text")
              numText.setAttribute("x", bestP.x.toString())
              numText.setAttribute("y", bestP.y.toString())
              numText.setAttribute("text-anchor", bestP.anchor)
              numText.setAttribute("font-family", "Arial, sans-serif")
              numText.setAttribute("font-size", Math.max(12, Math.min(16, cellWidth * 0.27)).toString())
              numText.setAttribute("font-weight", "bold")
              numText.setAttribute("fill", cellColor)
              numText.setAttribute("dominant-baseline", "middle")
              numText.setAttribute("style", "text-shadow: 0 0 3px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.6)")
              numText.textContent = conn.order.toString()
              pxOverlayGroup.appendChild(numText)
            }
          })
        })

        // 7.6 - Rendu de la bulle de mire dans l'export
        const bubbleConfig = element.data?.bubbleConfig || {}
        if (bubbleConfig.visible !== false) {
          const bMireName = element.data?.name?.split(' - ')[0] || 'Mire'
          const bubbleTitle = bubbleConfig.title || bMireName
          const bLinkedPanel = element.data?.linkedPanel as any
          const bubbleWidth = bubbleConfig.width || 220
          const baseHeight = bLinkedPanel ? 52 : 40
          const bubbleHeight = bubbleConfig.height || baseHeight
          const bgColor = bubbleConfig.bgColor || 'white'
          const bTextColor = bubbleConfig.textColor || 'black'
          const titleFontSize = bubbleConfig.titleFontSize || 11
          const infoFontSize = bubbleConfig.infoFontSize || 9

          const bx = element.size.width / 2 - bubbleWidth / 2
          const by = -bubbleHeight - 8
          const scaleFactor = Math.min(bubbleWidth / 220, bubbleHeight / baseHeight)

          const bShadow = document.createElementNS("http://www.w3.org/2000/svg", "rect")
          bShadow.setAttribute("x", (bx + 2).toString())
          bShadow.setAttribute("y", (by + 2).toString())
          bShadow.setAttribute("width", bubbleWidth.toString())
          bShadow.setAttribute("height", bubbleHeight.toString())
          bShadow.setAttribute("fill", "rgba(0,0,0,0.15)")
          bShadow.setAttribute("rx", "10")
          group.appendChild(bShadow)

          const bBg = document.createElementNS("http://www.w3.org/2000/svg", "rect")
          bBg.setAttribute("x", bx.toString())
          bBg.setAttribute("y", by.toString())
          bBg.setAttribute("width", bubbleWidth.toString())
          bBg.setAttribute("height", bubbleHeight.toString())
          bBg.setAttribute("fill", bgColor)
          bBg.setAttribute("stroke", "#e5e7eb")
          bBg.setAttribute("stroke-width", "1")
          bBg.setAttribute("rx", "10")
          group.appendChild(bBg)

          const bTitle = document.createElementNS("http://www.w3.org/2000/svg", "text")
          bTitle.setAttribute("x", (bx + bubbleWidth / 2).toString())
          bTitle.setAttribute("y", (by + bubbleHeight * 0.35).toString())
          bTitle.setAttribute("text-anchor", "middle")
          bTitle.setAttribute("dominant-baseline", "middle")
          bTitle.setAttribute("font-family", "Arial, sans-serif")
          bTitle.setAttribute("font-size", Math.max(8, titleFontSize * scaleFactor).toString())
          bTitle.setAttribute("font-weight", "bold")
          bTitle.setAttribute("fill", bTextColor)
          bTitle.textContent = bubbleTitle
          group.appendChild(bTitle)

          const bInfo = document.createElementNS("http://www.w3.org/2000/svg", "text")
          bInfo.setAttribute("x", (bx + 12).toString())
          bInfo.setAttribute("y", (by + bubbleHeight * 0.65).toString())
          bInfo.setAttribute("font-family", "Arial, sans-serif")
          bInfo.setAttribute("font-size", Math.max(6, infoFontSize * scaleFactor).toString())
          bInfo.setAttribute("fill", bTextColor)
          bInfo.textContent = (config.width || 1920) + '\u00D7' + (config.height || 1080) + 'px'
          group.appendChild(bInfo)

          const bGrid = document.createElementNS("http://www.w3.org/2000/svg", "text")
          bGrid.setAttribute("x", (bx + bubbleWidth - 12).toString())
          bGrid.setAttribute("y", (by + bubbleHeight * 0.65).toString())
          bGrid.setAttribute("text-anchor", "end")
          bGrid.setAttribute("font-family", "Arial, sans-serif")
          bGrid.setAttribute("font-size", Math.max(6, infoFontSize * scaleFactor).toString())
          bGrid.setAttribute("fill", bTextColor)
          bGrid.textContent = columns + '\u00D7' + rows + ' panneaux'
          group.appendChild(bGrid)
        }
      }

      exportSvg.appendChild(group)
    })

    // 4. Ajouter les câbles visuels de la page (cableRoutes uniquement)
    // Ces câbles sont créés visuellement par l'utilisateur
    // Helper: dessiner une flèche polygonale au bout d'une ligne
    const createArrowPolygon = (x1: number, y1: number, x2: number, y2: number, color: string, sw: number) => {
      const angle = Math.atan2(y2 - y1, x2 - x1)
      const arrowLen = sw * 4
      const arrowWid = sw * 2.5
      const tip = { x: x2, y: y2 }
      const base1 = { x: x2 - arrowLen * Math.cos(angle) + arrowWid * Math.sin(angle), y: y2 - arrowLen * Math.sin(angle) - arrowWid * Math.cos(angle) }
      const base2 = { x: x2 - arrowLen * Math.cos(angle) - arrowWid * Math.sin(angle), y: y2 - arrowLen * Math.sin(angle) + arrowWid * Math.cos(angle) }
      const poly = document.createElementNS("http://www.w3.org/2000/svg", "polygon")
      poly.setAttribute("points", `${tip.x},${tip.y} ${base1.x},${base1.y} ${base2.x},${base2.y}`)
      poly.setAttribute("fill", color)
      return poly
    }

    // Helper: créer un groupe ligne+flèche dans l'export
    const createExportLine = (x1: number, y1: number, x2: number, y2: number, color: string, opts?: { dashed?: boolean, markerEnd?: string, strokeWidth?: number }) => {
      const sw = opts?.strokeWidth || 2.5
      const g = document.createElementNS("http://www.w3.org/2000/svg", "g")
      const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
      // Si flèche, raccourcir la ligne pour ne pas dépasser sous le triangle
      const hasArrow = !!opts?.markerEnd
      let ex2 = x2, ey2 = y2
      if (hasArrow) {
        const angle = Math.atan2(y2 - y1, x2 - x1)
        const arrowLen = sw * 4
        ex2 = x2 - arrowLen * Math.cos(angle)
        ey2 = y2 - arrowLen * Math.sin(angle)
      }
      line.setAttribute("x1", x1.toString())
      line.setAttribute("y1", y1.toString())
      line.setAttribute("x2", ex2.toString())
      line.setAttribute("y2", ey2.toString())
      line.setAttribute("stroke", color)
      line.setAttribute("stroke-width", sw.toString())
      line.setAttribute("fill", "none")
      line.setAttribute("stroke-linecap", "round")
      if (opts?.dashed) line.setAttribute("stroke-dasharray", "5,5")
      g.appendChild(line)
      if (hasArrow) g.appendChild(createArrowPolygon(x1, y1, x2, y2, color, sw))
      return g
    }

    // Helper: obtenir le meilleur point d'ancrage sur un processeur (8 points)
    const getExportConnectionPoint = (element: WorkspaceElement, targetX: number, targetY: number) => {
      const bounds = (element.data as any)?.image
        ? getActualImageBounds(element)
        : { x: element.position.x, y: element.position.y, width: element.size.width, height: element.size.height }
      const pts = [
        { x: bounds.x, y: bounds.y },
        { x: bounds.x + bounds.width * 0.5, y: bounds.y },
        { x: bounds.x + bounds.width, y: bounds.y },
        { x: bounds.x + bounds.width, y: bounds.y + bounds.height * 0.5 },
        { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
        { x: bounds.x + bounds.width * 0.5, y: bounds.y + bounds.height },
        { x: bounds.x, y: bounds.y + bounds.height },
        { x: bounds.x, y: bounds.y + bounds.height * 0.5 },
      ]
      let best = pts[0], dmin = Infinity
      pts.forEach(p => { const d = Math.hypot(p.x - targetX, p.y - targetY); if (d < dmin) { dmin = d; best = p } })
      return best
    }

    // Helper: milieu du côté du panneau le plus proche de (fromX, fromY)
    const getExportPanelEdgePoint = (fromX: number, fromY: number, panel: WorkspaceElement) => {
      const cx = panel.position.x + panel.size.width / 2
      const cy = panel.position.y + panel.size.height / 2
      const dx = cx - fromX, dy = cy - fromY
      if (Math.abs(dx) >= Math.abs(dy)) {
        return { x: dx > 0 ? panel.position.x : panel.position.x + panel.size.width, y: cy }
      } else {
        return { x: cx, y: dy > 0 ? panel.position.y : panel.position.y + panel.size.height }
      }
    }

    // Charge électrique par armoire (liaisons armoire → armoire incluses) pour les labels d'export
    const exportElementsById = new Map(page.workspaceElements.map(e => [e.id, e]))
    const exportCabinetLoads = computeCabinetLoadMap(
      page.workspaceElements,
      page.powerConnections || [],
      page.mirePowerConnections || [],
      page.cableRoutes
    )

    page.cableRoutes.forEach(cable => {
      if (cable.points.length < 2) return

      const fromElement = page.workspaceElements.find(el => el.id === cable.from)
      const toElement = page.workspaceElements.find(el => el.id === cable.to)
      const isCabinetLink = isCabinetLinkCable(cable, exportElementsById)

      // Déterminer le numéro de port/outlet et la couleur depuis les connexions
      let portNumber = 0
      let isFirstInChain = false
      // Entité porteuse de la couleur (processeur ou armoire) pour les overrides custom
      let colorEntityId: string | null = null

      if (cable.type === 'data') {
        // Data: chercher dans panelConnections ou mireConnections
        const panelConn = page.panelConnections.find(c => c.panelId === cable.to)
        if (panelConn) { portNumber = panelConn.port; colorEntityId = panelConn.processorId; isFirstInChain = fromElement?.type === 'processor' }
        if (!portNumber) {
          const isMireCell = cable.to.includes('_cell_')
          if (isMireCell) {
            const mireConn = page.mireConnections?.find(c => {
              const cellId = `${c.imageId}_cell_${c.gridPosition.row}_${c.gridPosition.col}`
              return cellId === cable.to
            })
            if (mireConn) { portNumber = mireConn.port; colorEntityId = mireConn.processorId; isFirstInChain = fromElement?.type === 'processor' && mireConn.order === 1 }
          }
        }
      } else if (cable.type === 'power') {
        // Power: chercher dans powerConnections ou mirePowerConnections
        const powerConn = page.powerConnections.find(c => c.panelId === cable.to)
        if (powerConn) { portNumber = powerConn.outlet; colorEntityId = powerConn.cabinetId; isFirstInChain = fromElement?.type === 'cabinet' }
        if (!portNumber) {
          const isMireCell = cable.to.includes('_cell_')
          if (isMireCell) {
            const mirePConn = page.mirePowerConnections?.find(c => {
              const cellId = `${c.imageId}_cell_${c.gridPosition.row}_${c.gridPosition.col}`
              return cellId === cable.to
            })
            if (mirePConn) { portNumber = mirePConn.outlet; colorEntityId = mirePConn.cabinetId; isFirstInChain = fromElement?.type === 'cabinet' && mirePConn.order === 1 }
          }
        }
      }

      // Skip les câbles processeur/armoire → 1er panneau (déjà rendus en 4b)
      if (isFirstInChain) return

      const cableColor = portNumber > 0
        ? (cable.type === 'power'
            ? getOutletColorForExport(portNumber, colorEntityId)
            : getPortColorForExport(portNumber, colorEntityId))
        : (cable.color || "#2563EB")
      const markerUrl = portNumber > 0
        ? `url(#${ensureArrowMarker(cableColor, portNumber)})`
        : `url(#${ensureArrowMarker(cableColor, 1)})`

      // Calculer strokeWidth adaptatif pour câbles mire (comme dans le workspace)
      let cableStrokeWidth = 2.5
      if (isCabinetLink) cableStrokeWidth = 3
      const isMireCable = cable.from.includes('_cell_') || cable.to.includes('_cell_')
      let mireOrder = 0

      if (isMireCable) {
        const cellId = cable.to.includes('_cell_') ? cable.to : cable.from
        const imageId = cellId.split('_cell_')[0]
        const mireImage = page.workspaceElements.find(e => e.id === imageId && e.type === 'image')
        if (mireImage && mireImage.data?.mireConfig) {
          const { columns, rows } = mireImage.data.mireConfig.ledWallMode
          const cellWidthCalc = mireImage.size.width / columns
          const cellHeightCalc = mireImage.size.height / rows
          const avgCellSize = (cellWidthCalc + cellHeightCalc) / 2
          cableStrokeWidth = Math.max(1, Math.min(6, avgCellSize * 0.05))
        }
        // Récupérer l'ordre pour l'alternance de flèches
        if (cable.type === 'data') {
          const mc = page.mireConnections?.find(c => `${c.imageId}_cell_${c.gridPosition.row}_${c.gridPosition.col}` === cable.to)
          if (mc) mireOrder = mc.order
        } else {
          const mpc = page.mirePowerConnections?.find(c => `${c.imageId}_cell_${c.gridPosition.row}_${c.gridPosition.col}` === cable.to)
          if (mpc) mireOrder = mpc.order
        }
      }

      // Déterminer le markerEnd : pas de flèche pour 1er câble, alternance pour mires
      let finalMarkerEnd: string | undefined = markerUrl
      if (isFirstInChain) {
        finalMarkerEnd = undefined
      } else if (isMireCable && mireOrder > 0 && mireOrder % 2 !== 0) {
        finalMarkerEnd = undefined // Pas de flèche sur ordres impairs (comme workspace)
      }

      // Calcul milieu-de-côté par élément (uniquement pour les panneaux)
      let p0x = cable.points[0].x, p0y = cable.points[0].y
      let p1x = cable.points[cable.points.length - 1].x, p1y = cable.points[cable.points.length - 1].y
      const fromIsPanel = fromElement?.type === 'panel'
      const toIsPanel = toElement?.type === 'panel'
      if (fromIsPanel || toIsPanel) {
        const refFx = fromElement ? fromElement.position.x + fromElement.size.width / 2 : p0x
        const refFy = fromElement ? fromElement.position.y + fromElement.size.height / 2 : p0y
        const refTx = toElement ? toElement.position.x + toElement.size.width / 2 : p1x
        const refTy = toElement ? toElement.position.y + toElement.size.height / 2 : p1y
        const ddx = refTx - refFx, ddy = refTy - refFy
        const ddist = Math.sqrt(ddx * ddx + ddy * ddy) || 1
        if (fromIsPanel) {
          if (Math.abs(ddx) >= Math.abs(ddy)) {
            const sideOffset = (ddy / ddist) * fromElement!.size.height * 0.25
            p0x = ddx > 0 ? fromElement!.position.x + fromElement!.size.width : fromElement!.position.x
            p0y = refFy + sideOffset
          } else {
            const sideOffset = (ddx / ddist) * fromElement!.size.width * 0.25
            p0x = refFx + sideOffset
            p0y = ddy > 0 ? fromElement!.position.y + fromElement!.size.height : fromElement!.position.y
          }
        }
        if (toIsPanel) {
          if (Math.abs(ddx) >= Math.abs(ddy)) {
            const sideOffset = (ddy / ddist) * toElement!.size.height * 0.25
            p1x = ddx > 0 ? toElement!.position.x : toElement!.position.x + toElement!.size.width
            p1y = refTy + sideOffset
          } else {
            const sideOffset = (ddx / ddist) * toElement!.size.width * 0.25
            p1x = refTx + sideOffset
            p1y = ddy > 0 ? toElement!.position.y : toElement!.position.y + toElement!.size.height
          }
        }
      }

      // Flèche au milieu du segment pour câbles panneau non-mire
      if (!isMireCable && finalMarkerEnd) {
        const lineEl = createExportLine(p0x, p0y, p1x, p1y, cableColor, { strokeWidth: cableStrokeWidth })
        exportSvg.appendChild(lineEl)
        const midX = (p0x + p1x) / 2, midY = (p0y + p1y) / 2
        const ang = Math.atan2(p1y - p0y, p1x - p0x)
        const aLen = cableStrokeWidth * 3, aWid = cableStrokeWidth * 2
        const poly = document.createElementNS("http://www.w3.org/2000/svg", "polygon")
        poly.setAttribute("points", `${midX + aLen * Math.cos(ang)},${midY + aLen * Math.sin(ang)} ${midX - aLen * 0.5 * Math.cos(ang) + aWid * Math.sin(ang)},${midY - aLen * 0.5 * Math.sin(ang) - aWid * Math.cos(ang)} ${midX - aLen * 0.5 * Math.cos(ang) - aWid * Math.sin(ang)},${midY - aLen * 0.5 * Math.sin(ang) + aWid * Math.cos(ang)}`)
        poly.setAttribute("fill", cableColor)
        exportSvg.appendChild(poly)
      } else {
        const line = createExportLine(p0x, p0y, p1x, p1y, cableColor, { markerEnd: finalMarkerEnd, strokeWidth: cableStrokeWidth })
        exportSvg.appendChild(line)
      }

      // Liaison armoire → armoire : badge de charge transitant par le lien (kW)
      if (isCabinetLink) {
        const linkLoadW = exportCabinetLoads.total.get(cable.to) || 0
        const sourceEl = exportElementsById.get(cable.from)
        const sourceMaxW = sourceEl?.type === 'cabinet' ? (sourceEl.data as PowerCabinet).maxPower : 0
        const overloaded = sourceMaxW > 0 && (exportCabinetLoads.total.get(cable.from) || 0) > sourceMaxW
        const lx = (p0x + p1x) / 2, ly = (p0y + p1y) / 2 - 10
        const labelGroup = document.createElementNS("http://www.w3.org/2000/svg", "g")
        const labelRect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
        labelRect.setAttribute("x", (lx - 24).toString())
        labelRect.setAttribute("y", (ly - 8).toString())
        labelRect.setAttribute("width", "48")
        labelRect.setAttribute("height", "16")
        labelRect.setAttribute("rx", "4")
        labelRect.setAttribute("fill", overloaded ? "#DC2626" : "#1F2937")
        labelRect.setAttribute("stroke", cableColor)
        labelRect.setAttribute("stroke-width", "1")
        const labelText = document.createElementNS("http://www.w3.org/2000/svg", "text")
        labelText.setAttribute("x", lx.toString())
        labelText.setAttribute("y", ly.toString())
        labelText.setAttribute("text-anchor", "middle")
        labelText.setAttribute("dominant-baseline", "central")
        labelText.setAttribute("font-size", "9")
        labelText.setAttribute("font-weight", "600")
        labelText.setAttribute("fill", "#FFFFFF")
        labelText.textContent = `${(linkLoadW / 1000).toFixed(1)} kW`
        labelGroup.appendChild(labelRect)
        labelGroup.appendChild(labelText)
        exportSvg.appendChild(labelGroup)
      }
    })

    // 4b. Ajouter les liaisons processeur/armoire → premier panneau avec ancrage dynamique
    if (showExportProcessorLink) {
    // DATA: processeur → premier panneau de chaque port
    const dataProcessors = page.workspaceElements.filter(e => e.type === 'processor')
    dataProcessors.forEach(processor => {
      const processorData = processor.data as LEDProcessor
      const numPorts = typeof processorData?.outputPorts === 'number' ? processorData.outputPorts : 0
      for (let portNum = 1; portNum <= numPorts; portNum++) {
        // Panneaux standard
        const firstConn = page.panelConnections.find(c => c.processorId === processor.id && c.port === portNum && c.order === 1)
        if (firstConn) {
          const panel = page.workspaceElements.find(e => e.id === firstConn.panelId)
          if (panel) {
            const pcx = panel.position.x + panel.size.width / 2
            const pcy = panel.position.y + panel.size.height / 2
            const start = getExportConnectionPoint(processor, pcx, pcy)
            const end = getExportPanelEdgePoint(start.x, start.y, panel)
            const color = getPortColorForExport(portNum, processor.id)
            exportSvg.appendChild(createExportLine(start.x, start.y, end.x, end.y, color, { dashed: true }))
          }
        }
        // Mires
        const firstMireConn = page.mireConnections?.find(c => c.processorId === processor.id && c.port === portNum && c.order === 1)
        if (firstMireConn) {
          const mireImg = page.workspaceElements.find(e => e.id === firstMireConn.imageId && e.type === 'image')
          if (mireImg && mireImg.data?.mireConfig) {
            const mConfig = mireImg.data.mireConfig
            const { columns: mCols, rows: mRows } = mConfig.ledWallMode
            const cellW = mireImg.size.width / mCols
            const cellH = mireImg.size.height / mRows
            const cx = mireImg.position.x + (firstMireConn.gridPosition.col - 1) * cellW + cellW / 2
            const cy = mireImg.position.y + (firstMireConn.gridPosition.row - 1) * cellH + cellH / 2
            const start = getExportConnectionPoint(processor, cx, cy)
            const color = getPortColorForExport(portNum, processor.id)
            exportSvg.appendChild(createExportLine(start.x, start.y, cx, cy, color, { dashed: true }))
          }
        }
      }
    })

    // POWER: armoire → premier panneau de chaque prise
    const cabinets = page.workspaceElements.filter(e => e.type === 'cabinet')
    cabinets.forEach(cab => {
      const cabinetData = cab.data as PowerCabinet
      for (let outlet = 1; outlet <= (cabinetData?.outlets || 12); outlet++) {
        const firstConn = page.powerConnections.find(c => c.cabinetId === cab.id && c.outlet === outlet && c.order === 1)
        if (firstConn) {
          const panel = page.workspaceElements.find(e => e.id === firstConn.panelId)
          if (panel) {
            const pcx = panel.position.x + panel.size.width / 2
            const pcy = panel.position.y + panel.size.height / 2
            const start = getExportConnectionPoint(cab, pcx, pcy)
            const end = getExportPanelEdgePoint(start.x, start.y, panel)
            const color = getOutletColorForExport(outlet, cab.id)
            exportSvg.appendChild(createExportLine(start.x, start.y, end.x, end.y, color, { dashed: true }))
          }
        }
        // Mires power
        const firstMirePConn = page.mirePowerConnections?.find(c => c.cabinetId === cab.id && c.outlet === outlet && c.order === 1)
        if (firstMirePConn) {
          const mireImg = page.workspaceElements.find(e => e.id === firstMirePConn.imageId && e.type === 'image')
          if (mireImg && mireImg.data?.mireConfig) {
            const mConfig = mireImg.data.mireConfig
            const { columns: mCols, rows: mRows } = mConfig.ledWallMode
            const cellW = mireImg.size.width / mCols
            const cellH = mireImg.size.height / mRows
            const cx = mireImg.position.x + (firstMirePConn.gridPosition.col - 1) * cellW + cellW / 2
            const cy = mireImg.position.y + (firstMirePConn.gridPosition.row - 1) * cellH + cellH / 2
            const start = getExportConnectionPoint(cab, cx, cy)
            const color = getOutletColorForExport(outlet, cab.id)
            exportSvg.appendChild(createExportLine(start.x, start.y, cx, cy, color, { dashed: true }))
          }
        }
      }
    })
    } // fin showExportProcessorLink

    // 4c. Générer les câbles inter-cellules mire explicitement depuis les connexions
    // DATA mire: câbles entre cellules consécutives (order N → order N+1)
    // Helper: créer une ligne+flèche explicite pour câbles mire (sans marker-end)
    const createMireLine = (x1: number, y1: number, x2: number, y2: number, color: string, opts?: { markerEnd?: string, strokeWidth?: number }) => {
      const sw = opts?.strokeWidth || 2
      const g = document.createElementNS("http://www.w3.org/2000/svg", "g")
      const hasArrow = !!opts?.markerEnd
      let ex2 = x2, ey2 = y2
      if (hasArrow) {
        const angle = Math.atan2(y2 - y1, x2 - x1)
        const arrowLen = sw * 4
        ex2 = x2 - arrowLen * Math.cos(angle)
        ey2 = y2 - arrowLen * Math.sin(angle)
      }
      const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
      line.setAttribute("x1", x1.toString())
      line.setAttribute("y1", y1.toString())
      line.setAttribute("x2", ex2.toString())
      line.setAttribute("y2", ey2.toString())
      line.setAttribute("stroke", color)
      line.setAttribute("stroke-width", sw.toString())
      line.setAttribute("fill", "none")
      line.setAttribute("stroke-linecap", "round")
      g.appendChild(line)
      if (hasArrow) g.appendChild(createArrowPolygon(x1, y1, x2, y2, color, sw))
      return g
    }

    const mireImages = page.workspaceElements.filter(e => e.type === 'image' && e.data?.mireConfig)
    mireImages.forEach(mireImg => {
      const config = mireImg.data!.mireConfig!
      const { columns, rows } = config.ledWallMode
      const cellW = mireImg.size.width / columns
      const cellH = mireImg.size.height / rows
      const avgCellSize = (cellW + cellH) / 2
      const mireStrokeWidth = Math.max(1, Math.min(6, avgCellSize * 0.05))

      const getCellCenter = (row: number, col: number) => ({
        x: mireImg.position.x + (col - 1) * cellW + cellW / 2,
        y: mireImg.position.y + (row - 1) * cellH + cellH / 2
      })

      // Data mire connections
      if (page.mireConnections) {
        const dataConns = page.mireConnections.filter(c => c.imageId === mireImg.id)
        // Group by processor+port
        const groups = new Map<string, typeof dataConns>()
        dataConns.forEach(c => {
          const key = `${c.processorId}-${c.port}`
          if (!groups.has(key)) groups.set(key, [])
          groups.get(key)!.push(c)
        })
        groups.forEach((conns, _key) => {
          const sorted = [...conns].sort((a, b) => a.order - b.order)
          const portNum = sorted[0]?.port || 1
          const color = getPortColorForExport(portNum, sorted[0]?.processorId)
          const markerUrl = `url(#${ensureArrowMarker(color, portNum)})`
          for (let i = 1; i < sorted.length; i++) {
            const prev = sorted[i - 1]
            const curr = sorted[i]
            const from = getCellCenter(prev.gridPosition.row, prev.gridPosition.col)
            const to = getCellCenter(curr.gridPosition.row, curr.gridPosition.col)
            // Alternance flèches: flèche sur ordres pairs uniquement (comme workspace)
            const showArrow = curr.order % 2 === 0
            exportSvg.appendChild(createMireLine(from.x, from.y, to.x, to.y, color, {
              strokeWidth: mireStrokeWidth,
              markerEnd: showArrow ? markerUrl : undefined
            }))
          }
        })
      }

      // Power mire connections
      if (page.mirePowerConnections) {
        const powerConns = page.mirePowerConnections.filter(c => c.imageId === mireImg.id)
        const groups = new Map<string, typeof powerConns>()
        powerConns.forEach(c => {
          const key = `${c.cabinetId}-${c.outlet}`
          if (!groups.has(key)) groups.set(key, [])
          groups.get(key)!.push(c)
        })
        groups.forEach((conns, _key) => {
          const sorted = [...conns].sort((a, b) => a.order - b.order)
          const outletNum = sorted[0]?.outlet || 1
          const color = getOutletColorForExport(outletNum, sorted[0]?.cabinetId)
          const markerUrl = `url(#${ensureArrowMarker(color, outletNum)})`
          for (let i = 1; i < sorted.length; i++) {
            const prev = sorted[i - 1]
            const curr = sorted[i]
            const from = getCellCenter(prev.gridPosition.row, prev.gridPosition.col)
            const to = getCellCenter(curr.gridPosition.row, curr.gridPosition.col)
            const showArrow = curr.order % 2 === 0
            exportSvg.appendChild(createMireLine(from.x, from.y, to.x, to.y, color, {
              strokeWidth: mireStrokeWidth,
              markerEnd: showArrow ? markerUrl : undefined
            }))
          }
        })
      }
    })

    // Ajouter l'overlay PX/RX/order par dessus les câbles
    exportSvg.appendChild(pxOverlayGroup)

    // 4d. Lignes de connexion Main↔Backup processeurs (comme le workspace)
    Object.entries(backupProcessors).forEach(([primaryId, backupId]) => {
      const primaryProcessor = page.workspaceElements.find(e => e.id === primaryId && e.type === 'processor')
      const backupProcessor = page.workspaceElements.find(e => e.id === backupId && e.type === 'processor')
      if (primaryProcessor && backupProcessor) {
        const primaryBounds = (primaryProcessor.data as LEDProcessor).image
          ? getActualImageBounds(primaryProcessor)
          : { x: primaryProcessor.position.x, y: primaryProcessor.position.y, width: primaryProcessor.size.width, height: primaryProcessor.size.height }
        const backupBounds = (backupProcessor.data as LEDProcessor).image
          ? getActualImageBounds(backupProcessor)
          : { x: backupProcessor.position.x, y: backupProcessor.position.y, width: backupProcessor.size.width, height: backupProcessor.size.height }
        const x1 = primaryBounds.x + primaryBounds.width / 2
        const y1 = primaryBounds.y + primaryBounds.height / 2
        const x2 = backupBounds.x + backupBounds.width / 2
        const y2 = backupBounds.y + backupBounds.height / 2
        const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
        line.setAttribute("x1", x1.toString())
        line.setAttribute("y1", y1.toString())
        line.setAttribute("x2", x2.toString())
        line.setAttribute("y2", y2.toString())
        line.setAttribute("stroke", "#3B82F6")
        line.setAttribute("stroke-width", "2")
        line.setAttribute("stroke-dasharray", "4,4")
        line.setAttribute("opacity", "0.6")
        exportSvg.appendChild(line)
      }
    })

    // 5. Intégrer les images des processeurs en Data URI
    const processorImages = exportSvg.querySelectorAll('image')
    const imagePromises = Array.from(processorImages).map(image => {
      return new Promise<SVGImageElement>(async (resolve) => {
        const newImage = image.cloneNode(true) as SVGImageElement
        const href = image.getAttribute('href')
        if (href && !href.startsWith('data:')) {
          try {
            const response = await fetch(href)
            if (!response.ok) {
              resolve(newImage)
              return
            }
            const blob = await response.blob()
            const reader = new FileReader()
            reader.onloadend = () => {
              newImage.setAttribute('href', reader.result as string)
              resolve(newImage)
            }
            reader.onerror = () => resolve(newImage)
            reader.readAsDataURL(blob)
          } catch (e) {
            resolve(newImage)
          }
        } else {
          resolve(newImage)
        }
      })
    })

    const embeddedImages = await Promise.all(imagePromises)
    embeddedImages.forEach((img, index) => {
      const oldImage = processorImages[index]
      if (oldImage && oldImage.parentNode) {
        oldImage.parentNode.replaceChild(img, oldImage)
      }
    })

    // 6. Ajouter le cartouche avec la marge de 7mm et le numéro de page
    const cartoucheGroup = document.createElementNS("http://www.w3.org/2000/svg", "g")
    cartoucheGroup.innerHTML = `
      <rect x="-426.45" y="269.35" width="852.9" height="23.65" fill="white" stroke="black" stroke-width="0.5"/>
      <line x1="-320" y1="269.35" x2="-320" y2="293" stroke="black" stroke-width="0.3"/>
      <line x1="-213" y1="269.35" x2="-213" y2="293" stroke="black" stroke-width="0.3"/>
      <line x1="-106" y1="269.35" x2="-106" y2="293" stroke="black" stroke-width="0.3"/>
      <line x1="1" y1="269.35" x2="1" y2="293" stroke="black" stroke-width="0.3"/>
      <line x1="108" y1="269.35" x2="108" y2="293" stroke="black" stroke-width="0.3"/>
      <line x1="215" y1="269.35" x2="215" y2="293" stroke="black" stroke-width="0.3"/>
      <line x1="322" y1="269.35" x2="322" y2="293" stroke="black" stroke-width="0.3"/>
      <text x="-423" y="277" font-family="Arial" font-size="5" font-weight="bold">${t("export.agencyClient")} :</text>
      <text x="-423" y="285" font-family="Arial" font-size="5">${cartouche.agence}</text>
      <text x="-423" y="290" font-family="Arial" font-size="5">${cartouche.client}</text>
      <text x="-317" y="277" font-family="Arial" font-size="5" font-weight="bold">${t("export.event")} :</text>
      <text x="-317" y="285" font-family="Arial" font-size="5">${cartouche.evenement}</text>
      <text x="-210" y="277" font-family="Arial" font-size="5" font-weight="bold">${t("export.location")} :</text>
      <text x="-210" y="285" font-family="Arial" font-size="5">${cartouche.lieu}</text>
      <text x="-103" y="277" font-family="Arial" font-size="5" font-weight="bold">${t("export.suite")} :</text>
      <text x="-103" y="285" font-family="Arial" font-size="5">${cartouche.suite}</text>
      <text x="4" y="277" font-family="Arial" font-size="5" font-weight="bold">${t("export.what")} :</text>
      <text x="4" y="285" font-family="Arial" font-size="5">${cartouche.quoi}</text>
      <text x="111" y="277" font-family="Arial" font-size="5" font-weight="bold">${t("export.synopticLegend")} :</text>
      <text x="111" y="285" font-family="Arial" font-size="5">${page.name}</text>
      <text x="218" y="277" font-family="Arial" font-size="5" font-weight="bold">${t("export.date")} :</text>
      <text x="218" y="285" font-family="Arial" font-size="5">${cartouche.date}</text>
      <text x="325" y="277" font-family="Arial" font-size="5" font-weight="bold">${t("export.from")} :</text>
      <text x="325" y="285" font-family="Arial" font-size="5">${cartouche.de}</text>
      <text x="395" y="285" font-family="Arial" font-size="5" font-weight="bold">Page ${pageNumber}/${totalPages}</text>
    `
    exportSvg.appendChild(cartoucheGroup)

    return exportSvg
  }

  // Fonction d'assistance pour créer le SVG d'export de la page courante (pour compatibilité)
  const createExportSvg = async (): Promise<SVGSVGElement> => {
    if (isPowerMode) {
      const pageIndex = powerPages.findIndex(p => p.id === currentPowerPageId)
      return createExportSvgForPowerPage(currentPowerPage, pageIndex + 1, powerPages.length)
    } else {
      const pageIndex = dataPages.findIndex(p => p.id === currentDataPageId)
      return createExportSvgForDataPage(currentDataPage, pageIndex + 1, dataPages.length)
    }
  }

  // Générer le nom de fichier d'export à partir du cartouche
  const getExportFilename = (suffix: string, ext: string) => {
    const parts = [cartouche.client, cartouche.evenement, cartouche.lieu, cartouche.date].filter(s => s && s.trim())
    if (parts.length >= 2) {
      const sanitized = parts.map(s => s.trim().replace(/[^a-zA-Z0-9\u00C0-\u024F -]/g, '').replace(/\s+/g, '_')).join('_')
      return suffix ? sanitized + '_' + suffix + '.' + ext : sanitized + '.' + ext
    }
    return suffix ? 'synoptique-' + suffix + '-' + Date.now() + '.' + ext : 'synoptique-' + Date.now() + '.' + ext
  }

  const exportToSVG = async () => {
    try {
      setIsExporting(true)
      setExportProgress('Export SVG en cours...')
      const exportSvg = await createExportSvg()

      const svgString = new XMLSerializer().serializeToString(exportSvg)

      if (isTauri) {
        // Tauri: utiliser le dialogue de sauvegarde
        const { save } = await import('@tauri-apps/plugin-dialog')
        const { writeTextFile } = await import('@tauri-apps/plugin-fs')
        
        const filePath = await save({
          filters: [{
            name: 'SVG Image',
            extensions: ['svg']
          }],
          defaultPath: getExportFilename('', 'svg')
        })

        if (filePath) {
          await writeTextFile(filePath, svgString)
          setIsExporting(false)
          setShowExportSuccess(true)
          
          // ✅ PostHog: Track SVG export
          posthog.capture('synoptic:export_svg', {
            is_pro: isPro,
            is_power_mode: isPowerMode,
            data_pages_count: dataPages.length,
            power_pages_count: powerPages.length,
            show_cartouche: showCartouche,
          });
          
          setTimeout(() => setShowExportSuccess(false), 2000)
        } else {
          setIsExporting(false)
        }
      } else {
        // Web: téléchargement direct
        const svgBlob = new Blob([svgString], {
          type: "image/svg+xml;charset=utf-8"
        })
        const url = URL.createObjectURL(svgBlob)

        const link = document.createElement("a")
        link.download = getExportFilename('', 'svg')
        link.href = url
        link.click()

        URL.revokeObjectURL(url)
        setIsExporting(false)
        setShowExportSuccess(true)
        
        // ✅ PostHog: Track PNG export
        posthog.capture('synoptic:export_png', {
          is_pro: isPro,
          is_power_mode: isPowerMode,
          data_pages_count: dataPages.length,
          power_pages_count: powerPages.length,
          show_cartouche: showCartouche,
        });
        
        setTimeout(() => setShowExportSuccess(false), 2000)
      }
    } catch (error) {
      setIsExporting(false)
      toast.error(t('alert.svgExportError'))
    }
  }

  // Helper pour convertir un SVG d'export en PNG data URL avec watermark optionnel
  const svgToPngWithWatermark = (svgElement: SVGSVGElement, addWatermark: boolean): Promise<string> => {
    return new Promise((resolve, reject) => {
      const svgCopy = svgElement.cloneNode(true) as SVGSVGElement
      svgCopy.setAttribute("width", "860")
      svgCopy.setAttribute("height", "600")

      const canvas = document.createElement("canvas")
      const ctx = canvas.getContext("2d")
      if (!ctx) { reject(new Error("Le contexte Canvas n'est pas disponible")); return }

      const img = new Image()
      const svgBlob = new Blob([new XMLSerializer().serializeToString(svgCopy)], { type: "image/svg+xml" })
      const url = URL.createObjectURL(svgBlob)

      img.onload = () => {
        canvas.width = 860 * 4
        canvas.height = 600 * 4
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.scale(4, 4)
        ctx.drawImage(img, 0, 0)

        if (addWatermark) {
          ctx.save()
          ctx.globalAlpha = 0.15
          ctx.font = 'bold 80px Arial'
          ctx.fillStyle = '#000000'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.translate(430, 300)
          ctx.rotate(-Math.PI / 6)
          ctx.fillText('VisualGrid', 0, -60)
          ctx.fillText('DEMO', 0, 60)
          ctx.restore()
        }

        const dataUrl = canvas.toDataURL("image/png", 1.0)
        URL.revokeObjectURL(url)
        resolve(dataUrl)
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error("Erreur lors du chargement de l'image SVG"))
      }

      img.src = url
    })
  }

  // Fonction d'export PNG
  const exportToImage = async () => {
    try {
      setIsExporting(true)
      setExportProgress('Export PNG en cours...')
      const isDemo = demoMode.state.isDemo && !isPro

      // Helper pour exporter un PNG
      const exportPngFile = async (dataUrl: string, filename: string) => {
        if (isTauri) {
          const { writeFile } = await import('@tauri-apps/plugin-fs')
          const { save } = await import('@tauri-apps/plugin-dialog')
          
          const filePath = await save({
            filters: [{ name: 'PNG Image', extensions: ['png'] }],
            defaultPath: filename
          })

          if (filePath) {
            const base64Data = dataUrl.replace(/^data:image\/png;base64,/, '')
            const binaryData = atob(base64Data)
            const bytes = new Uint8Array(binaryData.length)
            for (let i = 0; i < binaryData.length; i++) {
              bytes[i] = binaryData.charCodeAt(i)
            }
            await writeFile(filePath, bytes)
            return true
          }
          return false
        } else {
          const link = document.createElement("a")
          link.download = filename
          link.href = dataUrl
          link.click()
          return true
        }
      }

      if (isDemo) {
        // Export Data page
        const dataPage = dataPages[0]
        if (dataPage) {
          const dataSvg = await createExportSvgForDataPage(dataPage, 1, 1)
          const dataUrl = await svgToPngWithWatermark(dataSvg, true)
          await exportPngFile(dataUrl, getExportFilename('data', 'png'))
        }

        // Petit délai pour éviter que le navigateur bloque les téléchargements multiples
        await new Promise(resolve => setTimeout(resolve, 500))

        // Export Power page
        const powerPage = powerPages[0]
        if (powerPage) {
          const powerSvg = await createExportSvgForPowerPage(powerPage, 1, 1)
          const powerUrl = await svgToPngWithWatermark(powerSvg, true)
          await exportPngFile(powerUrl, getExportFilename('power', 'png'))
        }

        if (onboarding.state.isActive) {
          onboarding.completeAction('png-exported')
        }
      } else {
        // Export toutes les pages Data
        for (let i = 0; i < dataPages.length; i++) {
          const page = dataPages[i]
          const pageSvg = await createExportSvgForDataPage(page, i + 1, dataPages.length + powerPages.length)
          const pageUrl = await svgToPngWithWatermark(pageSvg, false)
          
          const filename = `synoptique-${page.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.png`
          await exportPngFile(pageUrl, filename)
          
          // Délai entre chaque export
          if (i < dataPages.length - 1 || powerPages.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 500))
          }
        }

        // Export toutes les pages Power
        for (let i = 0; i < powerPages.length; i++) {
          const page = powerPages[i]
          const pageSvg = await createExportSvgForPowerPage(page, dataPages.length + i + 1, dataPages.length + powerPages.length)
          const pageUrl = await svgToPngWithWatermark(pageSvg, false)
          
          const filename = `synoptique-${page.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.png`
          await exportPngFile(pageUrl, filename)
          
          // Délai entre chaque export
          if (i < powerPages.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500))
          }
        }

        if (onboarding.state.isActive) {
          onboarding.completeAction('png-exported')
        }
      }
      setIsExporting(false)
      setShowExportSuccess(true)
      setTimeout(() => setShowExportSuccess(false), 2000)
    } catch (error) {
      setIsExporting(false)
      toast.error(t('synoptic.toast.pngExportError'))
    }
  }

  // Fonction d'export PDF multi-pages
  const exportToPDF = async () => {
    try {
      setIsExporting(true)
      setExportProgress('Export PDF en cours...')
      const totalPages = dataPages.length + powerPages.length

      const jsPDFModule = await import('jspdf')
      const jsPDF = jsPDFModule.default

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [860, 600]
      })

      const svgToDataUrl = (svgElement: SVGSVGElement): Promise<string> => {
        return new Promise((resolve, reject) => {
          const canvas = document.createElement("canvas")
          const ctx = canvas.getContext("2d")
          if (!ctx) {
            reject(new Error("Le contexte Canvas n'est pas disponible"))
            return
          }

          const svgCopy = svgElement.cloneNode(true) as SVGSVGElement
          svgCopy.setAttribute("width", "860")
          svgCopy.setAttribute("height", "600")

          const img = new Image()
          const svgBlob = new Blob([new XMLSerializer().serializeToString(svgCopy)], { type: "image/svg+xml" })
          const url = URL.createObjectURL(svgBlob)

          img.onload = () => {
            canvas.width = 860 * 4
            canvas.height = 600 * 4
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'
            ctx.scale(4, 4)
            ctx.drawImage(img, 0, 0)

            const imgData = canvas.toDataURL("image/png", 1.0)
            URL.revokeObjectURL(url)
            resolve(imgData)
          }

          img.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error("Erreur lors du chargement de l'image SVG"))
          }

          img.src = url
        })
      }

      let isFirstPage = true
      let pageCounter = 0

      for (let i = 0; i < dataPages.length; i++) {
        const page = dataPages[i]
        pageCounter++
        const exportSvg = await createExportSvgForDataPage(page, pageCounter, totalPages)
        const imgData = await svgToDataUrl(exportSvg)
        if (!isFirstPage) {
          pdf.addPage([860, 600], 'landscape')
        }
        isFirstPage = false
        pdf.addImage(imgData, 'PNG', 0, 0, 860, 600)
      }

      for (let i = 0; i < powerPages.length; i++) {
        const page = powerPages[i]
        pageCounter++
        const exportSvg = await createExportSvgForPowerPage(page, pageCounter, totalPages)
        const imgData = await svgToDataUrl(exportSvg)
        if (!isFirstPage) {
          pdf.addPage([860, 600], 'landscape')
        }
        isFirstPage = false
        pdf.addImage(imgData, 'PNG', 0, 0, 860, 600)
      }

      if (isTauri) {
        const { save } = await import('@tauri-apps/plugin-dialog')
        const { writeFile } = await import('@tauri-apps/plugin-fs')
        
        const filePath = await save({
          filters: [{ name: 'PDF Document', extensions: ['pdf'] }],
          defaultPath: getExportFilename('', 'pdf')
        })

        if (filePath) {
          const pdfBlob = pdf.output('blob')
          const arrayBuffer = await pdfBlob.arrayBuffer()
          const bytes = new Uint8Array(arrayBuffer)
          await writeFile(filePath, bytes)
        }
      } else {
        pdf.save(getExportFilename('', 'pdf'))
      }
      
      setIsExporting(false)
      setShowExportSuccess(true)
      
      // ✅ PostHog: Track PDF export
      posthog.capture('synoptic:export_pdf', {
        is_pro: isPro,
        is_power_mode: isPowerMode,
        data_pages_count: dataPages.length,
        power_pages_count: powerPages.length,
        show_cartouche: showCartouche,
      });
      
      setTimeout(() => setShowExportSuccess(false), 2000)

    } catch (error) {
      setIsExporting(false)
      toast.error(t('alert.pdfExportError'))
    }
  }

  return {
    createExportSvgForDataPage,
    createExportSvgForPowerPage,
    createExportSvg,
    exportToSVG,
    exportToImage,
    exportToPDF,
    getExportFilename,
  }
}
